import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { useDocumentTitle } from "../lib/title";

export type PageHeaderProps = {
  /**
   * On a record's page, the list it belongs to as a link back ("Catalog ›") or what the record is ("Order 90161").
   * Not the navigation group the page sits in: the sidebar already says that.
   */
  eyebrow?: ReactNode;
  title: string;
  /**
   * One sentence, or none. It may carry the page's counts ("8 open orders · 1 awaiting confirmation"), each a link
   * that applies the matching filter — the place a list page's figures live instead of a KPI row.
   */
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
};

/**
 * The top of a working page, kept short so the table starts high: an optional eyebrow, the title, the description on
 * the title's line when it fits (under it when not), and up to a handful of actions. No rule under it: the table's
 * header row or the first section's rule draws the line. The title keeps at least 18rem; when the actions do not fit
 * beside it they move below it, and they wrap among themselves — so a phone never squeezes the title into one word per
 * line or pushes a button off the screen. The title also names the browser tab ("Order inbox · Ghezaa Orders").
 */
export function PageHeader({ eyebrow, title, description, actions, className }: PageHeaderProps) {
  useDocumentTitle(title);
  return (
    <header className={cn("flex flex-wrap items-end justify-between gap-x-6 gap-y-3", className)}>
      <div className="min-w-0 flex-[1_1_18rem]">
        {eyebrow && <p className="mb-1 text-2xs font-medium uppercase tracking-wider text-ink-3">{eyebrow}</p>}
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h1 className="font-display text-2xl font-normal tracking-tight text-ink">{title}</h1>
          {description && <p className="max-w-prose text-sm text-ink-2">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
